import { FC, FormEvent, useState } from "react";

import { Stack } from "@mui/material";

import { CommunicationPreferenceOptionNameResult } from "./types";

import { TranslationKey, useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTextField } from "@/dashboard-design-system/TextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Lists } from "@/icons/outlined";

export type CommunicationPreferenceOptionNameModalMode = "create" | "rename";

const COPY_KEYS: Record<
  CommunicationPreferenceOptionNameModalMode,
  { title: TranslationKey<"dashboard">; description: TranslationKey<"dashboard">; submit: TranslationKey<"dashboard"> }
> = {
  create: {
    title: "communicationPreferences.createOption.title",
    description: "communicationPreferences.createOption.description",
    submit: "communicationPreferences.createOption.submit",
  },
  rename: {
    title: "communicationPreferences.renameOption.title",
    description: "communicationPreferences.renameOption.description",
    submit: "communicationPreferences.renameOption.submit",
  },
};

interface CommunicationPreferenceOptionNameModalProps {
  mode: CommunicationPreferenceOptionNameModalMode;
  initialName?: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<CommunicationPreferenceOptionNameResult>;
}

export const CommunicationPreferenceOptionNameModal: FC<CommunicationPreferenceOptionNameModalProps> = ({
  mode,
  initialName = "",
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslate();
  const copyKeys = COPY_KEYS[mode];
  const [name, setName] = useState(initialName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDuplicateName, setIsDuplicateName] = useState(false);

  const trimmedName = name.trim();
  const canSubmit = trimmedName !== "" && trimmedName !== initialName.trim();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // The submit would otherwise bubble through the portal into the question modal's form.
    event.stopPropagation();
    if (!canSubmit || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const result = await onSubmit(trimmedName).finally(() => setIsSubmitting(false));

    if (result === "duplicate") {
      setIsDuplicateName(true);
      return;
    }
    if (result === "saved") {
      onClose();
    }
  };

  return (
    <DashboardDialog open onClose={onClose} maxWidth="sm">
      <form onSubmit={(event) => void handleSubmit(event)}>
        <DashboardDialogBody
          slots={{
            header: <DashboardDialogHeader sx={{ pb: 0 }} icon={<Lists />} title={t("dashboard", copyKeys.title)} />,
            content: (
              <DashboardDialogContent sx={{ pt: 0 }}>
                <Stack spacing={2}>
                  <DashboardTypography variant="body1" vibe="text-neutral-quiet">
                    {t("dashboard", copyKeys.description)}
                  </DashboardTypography>
                  <DashboardTextField
                    autoFocus
                    label={t("dashboard", "communicationPreferences.optionName")}
                    placeholder={t("dashboard", "communicationPreferences.optionNamePlaceholder")}
                    value={name}
                    onChange={(event) => {
                      setName(event.target.value);
                      setIsDuplicateName(false);
                    }}
                    errorMessage={
                      isDuplicateName ? t("dashboard", "communicationPreferences.duplicateName") : undefined
                    }
                    data-test="communication-preference-option-name"
                  />
                </Stack>
              </DashboardDialogContent>
            ),
            footer: (
              <DashboardDialogFooter>
                <DashboardButton
                  type="submit"
                  disabled={!canSubmit}
                  isLoading={isSubmitting}
                  data-test="communication-preference-option-submit"
                >
                  {t("dashboard", copyKeys.submit)}
                </DashboardButton>
                <DashboardButton onClick={onClose}>{t("dashboard", "common.cancel")}</DashboardButton>
              </DashboardDialogFooter>
            ),
          }}
        />
      </form>
    </DashboardDialog>
  );
};
